import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { PageHeader } from '@/components/layout';
import { KPICard, TimeSeriesChart, BreakdownChart } from '@/components/analytics';
import { EmptyState, ErrorState } from '@/components/data-display';
import { analyticsApi } from '@/api/analytics';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Scan,
  QrCode,
  TrendingUp,
  MapPin,
  Plus,
  Eye,
  Pencil,
  MoreHorizontal,
  Link2,
  FileText,
  MousePointerClick,
  Users,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { apiV1 } from '@/api/client';

const PERIOD_LABEL = { '7d': 'last 7 days', '30d': 'last 30 days', '90d': 'last 90 days', '1y': 'last year' };

/**
 * Activity for the selected period, all in the caller's access scope:
 *   - every link type over time (QR scans, short URL clicks, landing page and
 *     bio page views), one legend entry each; this chart used to plot QR
 *     scans only, about 2% of real traffic;
 *   - period totals for the KPI card labels, so each card's label and its
 *     chart follow the date picker and the same scope;
 *   - device split over every link type.
 */
function useDashboardActivity(period) {
  const [state, setState] = useState({ series: null, summary: null, devices: [], loading: true, error: null });

  const load = React.useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const [series, summary, devices] = await Promise.all([
        analyticsApi.getActivityOverTime({ period, granularity: period === '1y' ? 'week' : 'day' }),
        analyticsApi.getActivitySummary({ period }),
        analyticsApi.getBreakdown('by-device', { period, type: 'all' }),
      ]);
      setState({
        series: series.data || null,
        summary: summary.data || null,
        devices: devices.data?.breakdown || [],
        loading: false,
        error: null,
      });
    } catch (err) {
      setState({ series: null, summary: null, devices: [], loading: false, error: err.message || 'Failed to load analytics' });
    }
  }, [period]);

  useEffect(() => { load(); }, [load]);
  return { ...state, refetch: load };
}

export default function Dashboard() {
  const [dateRange, setDateRange] = useState('7d');
  const {
    series: activitySeries,
    summary,
    devices: deviceBreakdown,
    loading: analyticsLoading,
    error,
    refetch,
  } = useDashboardActivity(dateRange);
  const periodLabel = PERIOD_LABEL[dateRange] || dateRange;
  const timeSeries = activitySeries?.data_points || [];
  const activityTotal = timeSeries.reduce((n, p) => n + (p.total || 0), 0);

  // State for real data
  const [qrCodes, setQRCodes] = useState([]);
  const [shortUrls, setShortUrls] = useState([]);
  const [landingPages, setLandingPages] = useState([]);
  const [leads, setLeads] = useState([]);
  // Partners never see lead contact details; they get the count only.
  const { isPartner } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalQRCodes: 0,
    totalShortUrls: 0,
    totalLandingPages: 0,
    totalLeads: 0,
  });

  // Fetch dashboard data
  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Lists are for the "recent" tables only (5 each). Totals come from the
      // per-resource stats endpoints; summing a 5-row list undercounts.
      const empty = { data: { data: [] } };
      const [qrRes, urlRes, pagesRes, leadsRes, qrStatsRes, urlStatsRes, pageStatsRes] = await Promise.all([
        apiV1.get('/qrcodes/?per_page=5').catch(() => empty),
        apiV1.get('/urls/?per_page=5').catch(() => empty),
        apiV1.get('/pages/?per_page=5').catch(() => empty),
        (isPartner ? apiV1.get('/leads/stats') : apiV1.get('/leads/?limit=5')).catch(() => empty),
        apiV1.get('/qrcodes/stats').catch(() => empty),
        apiV1.get('/urls/stats').catch(() => empty),
        apiV1.get('/pages/stats').catch(() => empty),
      ]);

      const listOf = (res) => {
        const d = res.data?.data;
        const items = d?.items || d || [];
        return Array.isArray(items) ? items.slice(0, 5) : [];
      };
      setQRCodes(listOf(qrRes));
      setShortUrls(listOf(urlRes));
      setLandingPages(listOf(pagesRes));
      setLeads(isPartner ? [] : listOf(leadsRes));

      const qrStats = qrStatsRes.data?.data || {};
      const urlStats = urlStatsRes.data?.data || {};
      const pageStats = pageStatsRes.data?.data || {};

      setStats({
        totalQRCodes: qrStats.total || 0,
        totalShortUrls: urlStats.total || 0,
        totalLandingPages: pageStats.total || 0,
        // Leads nest pagination under meta.
        totalLeads: isPartner
          ? leadsRes.data?.data?.total_leads || 0
          : leadsRes.data?.meta?.pagination?.total || 0,
      });

    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toLocaleString();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: 'default',
      published: 'default',
      draft: 'secondary',
      archived: 'outline',
      paused: 'outline',
    };
    return (
      <Badge variant={variants[status] || 'outline'} data-testid={`status-badge-${status}`}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </Badge>
    );
  };

  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Dashboard"
          description="Overview of your marketing performance"
        />
        <ErrorState message={error} onRetry={refetch} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <PageHeader
        title="Dashboard"
        description="Overview of your marketing performance"
        actions={
          <div className="flex items-center gap-2">
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-[140px]" data-testid="date-range-select">
                <SelectValue placeholder="Select range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
                <SelectItem value="90d">Last 90 days</SelectItem>
                <SelectItem value="1y">Last year</SelectItem>
              </SelectContent>
            </Select>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="create-new-button">
                  <Plus className="h-4 w-4 mr-2" />
                  Create New
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem asChild>
                  <Link to="/qrs/new">
                    <QrCode className="h-4 w-4 mr-2" />
                    QR Code
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/urls/new">
                    <Link2 className="h-4 w-4 mr-2" />
                    Short URL
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/pages/new">
                    <FileText className="h-4 w-4 mr-2" />
                    Landing Page
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        }
      />

      {/* KPI Cards: the big number is how many you have (all time, your
          scope); the label is activity in the selected period (same scope). */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="QR Codes"
          value={formatNumber(stats.totalQRCodes)}
          changeLabel={summary ? `${formatNumber(summary.scans)} scans, ${periodLabel}` : undefined}
          icon={QrCode}
          loading={loading}
        />
        <KPICard
          title="Short URLs"
          value={formatNumber(stats.totalShortUrls)}
          changeLabel={summary ? `${formatNumber(summary.clicks)} clicks, ${periodLabel}` : undefined}
          icon={Link2}
          loading={loading}
        />
        <KPICard
          title="Landing Pages"
          value={formatNumber(stats.totalLandingPages)}
          changeLabel={summary ? `${formatNumber(summary.page_views)} views, ${periodLabel}` : undefined}
          icon={FileText}
          loading={loading}
        />
        <KPICard
          title="Leads"
          value={formatNumber(stats.totalLeads)}
          changeLabel={summary ? `${formatNumber(summary.leads)} new, ${periodLabel}` : 'Total captured'}
          icon={Users}
          loading={loading}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {analyticsLoading || activityTotal > 0 ? (
          <TimeSeriesChart
            data={timeSeries}
            series={activitySeries?.series}
            title="Activity Over Time"
            description="QR scans, short URL clicks and page views per South African day. Bot traffic excluded."
            loading={analyticsLoading}
            className="lg:col-span-8"
          />
        ) : (
          <Card className="lg:col-span-8">
            <CardHeader>
              <CardTitle className="text-base font-medium">Activity Over Time</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                No activity in this period.
              </div>
            </CardContent>
          </Card>
        )}
        {deviceBreakdown && deviceBreakdown.length > 0 ? (
          <BreakdownChart
            data={deviceBreakdown}
            title="Devices"
            loading={analyticsLoading}
            className="lg:col-span-4"
          />
        ) : (
          <Card className="lg:col-span-4">
            <CardHeader>
              <CardTitle className="text-base font-medium">Device Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-48 flex items-center justify-center text-muted-foreground text-center">
                No device data yet
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Recent Items Tabs */}
      <Card data-testid="recent-items-card">
        <CardHeader>
          <CardTitle className="text-base font-medium">Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="qrcodes">
            <TabsList className="mb-4">
              <TabsTrigger value="qrcodes" data-testid="tab-qrcodes">
                <QrCode className="h-4 w-4 mr-2" />
                QR Codes
              </TabsTrigger>
              <TabsTrigger value="urls" data-testid="tab-urls">
                <Link2 className="h-4 w-4 mr-2" />
                Short URLs
              </TabsTrigger>
              <TabsTrigger value="pages" data-testid="tab-pages">
                <FileText className="h-4 w-4 mr-2" />
                Landing Pages
              </TabsTrigger>
              {!isPartner && (
                <TabsTrigger value="leads" data-testid="tab-leads">
                  <Users className="h-4 w-4 mr-2" />
                  Leads
                </TabsTrigger>
              )}
            </TabsList>

            {/* QR Codes Tab */}
            <TabsContent value="qrcodes">
              {loading ? (
                <LoadingSkeleton />
              ) : qrCodes.length === 0 ? (
                <EmptyState
                  icon={QrCode}
                  title="No QR codes yet"
                  description="Create your first QR code to start tracking scans"
                  actionLabel="Create QR Code"
                  onAction={() => window.location.href = '/qrs/new'}
                />
              ) : (
                <Table data-testid="qr-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Scans</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {qrCodes.map((qr) => (
                      <TableRow key={qr.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-3">
                            <div className="h-8 w-8 rounded bg-muted flex items-center justify-center">
                              <QrCode className="h-4 w-4 text-muted-foreground" />
                            </div>
                            {qr.name}
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(qr.status)}</TableCell>
                        <TableCell className="text-right">{formatNumber(qr.total_scans)}</TableCell>
                        <TableCell className="text-muted-foreground">{formatDate(qr.created_at)}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" asChild>
                            <Link to={`/qrs/${qr.id}`}>
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              <div className="mt-4 text-center">
                <Button variant="outline" size="sm" asChild>
                  <Link to="/qrs">View all QR Codes</Link>
                </Button>
              </div>
            </TabsContent>

            {/* Short URLs Tab */}
            <TabsContent value="urls">
              {loading ? (
                <LoadingSkeleton />
              ) : shortUrls.length === 0 ? (
                <EmptyState
                  icon={Link2}
                  title="No short URLs yet"
                  description="Create your first short URL to start tracking clicks"
                  actionLabel="Create Short URL"
                  onAction={() => window.location.href = '/urls/new'}
                />
              ) : (
                <Table data-testid="urls-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Short Code</TableHead>
                      <TableHead className="text-right">Clicks</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {shortUrls.map((url) => (
                      <TableRow key={url.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-3">
                            <div className="h-8 w-8 rounded bg-muted flex items-center justify-center">
                              <Link2 className="h-4 w-4 text-muted-foreground" />
                            </div>
                            {url.title || url.short_code}
                          </div>
                        </TableCell>
                        <TableCell>
                          <code className="text-sm bg-muted px-2 py-1 rounded">{url.short_code}</code>
                        </TableCell>
                        <TableCell className="text-right">{formatNumber(url.total_clicks)}</TableCell>
                        <TableCell className="text-muted-foreground">{formatDate(url.created_at)}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" asChild>
                            <Link to={`/urls/${url.id}`}>
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              <div className="mt-4 text-center">
                <Button variant="outline" size="sm" asChild>
                  <Link to="/urls">View all Short URLs</Link>
                </Button>
              </div>
            </TabsContent>

            {/* Landing Pages Tab */}
            <TabsContent value="pages">
              {loading ? (
                <LoadingSkeleton />
              ) : landingPages.length === 0 ? (
                <EmptyState
                  icon={FileText}
                  title="No landing pages yet"
                  description="Create your first landing page to capture leads"
                  actionLabel="Create Landing Page"
                  onAction={() => window.location.href = '/pages/new'}
                />
              ) : (
                <Table data-testid="pages-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Views</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {landingPages.map((page) => (
                      <TableRow key={page.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-3">
                            <div className="h-8 w-8 rounded bg-muted flex items-center justify-center">
                              <FileText className="h-4 w-4 text-muted-foreground" />
                            </div>
                            {page.title}
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(page.status)}</TableCell>
                        <TableCell className="text-right">{formatNumber(page.total_views)}</TableCell>
                        <TableCell className="text-muted-foreground">{formatDate(page.created_at)}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" asChild>
                            <Link to={`/pages/${page.id}`}>
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              <div className="mt-4 text-center">
                <Button variant="outline" size="sm" asChild>
                  <Link to="/pages">View all Landing Pages</Link>
                </Button>
              </div>
            </TabsContent>

            {/* Leads Tab */}
            <TabsContent value="leads">
              {loading ? (
                <LoadingSkeleton />
              ) : leads.length === 0 ? (
                <EmptyState
                  icon={Users}
                  title="No leads yet"
                  description="Leads will appear here when captured from your forms"
                />
              ) : (
                <Table data-testid="leads-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leads.map((lead) => (
                      <TableRow key={lead.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-3">
                            <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
                              <Users className="h-4 w-4 text-muted-foreground" />
                            </div>
                            {lead.name || 'Unknown'}
                          </div>
                        </TableCell>
                        <TableCell>{lead.email || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{lead.source || 'direct'}</Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{formatDate(lead.created_at)}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" asChild>
                            <Link to={`/leads/${lead.id}`}>
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              <div className="mt-4 text-center">
                <Button variant="outline" size="sm" asChild>
                  <Link to="/leads">View all Leads</Link>
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}

// Loading skeleton component
function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="flex items-center space-x-4">
          <Skeleton className="h-8 w-8 rounded" />
          <div className="space-y-2 flex-1">
            <Skeleton className="h-4 w-[200px]" />
            <Skeleton className="h-3 w-[100px]" />
          </div>
        </div>
      ))}
    </div>
  );
}
